import { AppLogoutConfirmComponent } from '@shared/components/app-logout-confirm/app-logout-confirm.component';
import { Component, inject, OnInit, OnDestroy, computed } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter, Subscription } from 'rxjs';
import { AppImageViewerComponent } from '../../shared/components/app-image-viewer/app-image-viewer.component';
import { AuthService } from '../../core/services/auth.service';
import { PermissionsService } from '../../core/services/permissions.service';
import { TransitionOverlayService } from '../../core/services/transition-overlay.service';

interface MenuItem {
  id: string;
  label: string;
  icon: string;
  route?: string;
  access?: 'all' | 'admin';
  /** Key del catálogo de permisos. Sin ella el ítem se muestra siempre. */
  permission?: string;
  /** Zona del menú: lo propio de la plataforma o los catálogos globales que alimentan a todos los negocios. */
  scope?: 'platform' | 'catalog';
  /** Rótulo que se dibuja encima del primer grupo de cada zona. */
  sectionStart?: string;
  children?: { label: string; route: string; icon: string; access: 'admin' | 'all'; permission?: string; queryParams?: Record<string, string> }[];
}

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [AppLogoutConfirmComponent, RouterOutlet, RouterLink, RouterLinkActive, CommonModule, AppImageViewerComponent],
  templateUrl: './admin-layout.html',
  styleUrl: './admin-layout.css'
})
export class AdminLayout implements OnInit, OnDestroy {
  private router = inject(Router);
  private authService = inject(AuthService);
  private perms = inject(PermissionsService);
  private overlayService = inject(TransitionOverlayService);
  private routerSub!: Subscription;

  // Sidebar Layout States
  isCollapsed = false;
  showMobileMenu = false;
  showProfileMenu = false;
  showLogoutConfirm = false;
  currentRoute = '';

  // Map route segments to Spanish display names
  private routeLabels: { [key: string]: string } = {
    'inicio': 'Inicio',
    'profile': 'Perfil',
    'ajustes': 'Ajustes',
    'businesses': 'Negocios',
    'establishments': 'Establecimientos',
    'emission-points': 'Puntos de Emisión',
    'invoices': 'Facturas',
    'roles': 'Roles',
    'permissions': 'Permisos',
    'systems': 'Sistemas',
    'users': 'Usuarios',
    'plans': 'Planes',
    'payments': 'Cobros y Pagos',
    'iva-rates': 'Tarifas de IVA',
    'retention-codes': 'Códigos de retención',
    'normative-parameters': 'Parámetros normativos',
    'financial-institutions': 'Instituciones financieras',
    'master-accounting-plans': 'Planes de cuentas',
    'persons': 'Clientes (personas)',
  };

  // Expanded submenus (mapped by MenuItem.id)
  expandedSubmenus: { [key: string]: boolean } = {};

  // State for hover tooltips/overlay when collapsed
  activeOverlayMenu: MenuItem | null = null;
  hoveredItemForTooltip: MenuItem | null = null;
  tooltipTop = 0;
  overlayTop = 0;
  overlayTimeout: any;

  /**
   * Menú lateral en dos zonas: lo propio de la plataforma (negocios, planes, clientes, acceso) y los catálogos globales (impuestos y
   * entidades financieras). Cada grupo se abre en sus pantallas; las pantallas sin permiso del usuario no aparecen.
   */
  menuItems: MenuItem[] = [
    { id: 'inicio', label: 'Inicio', icon: 'pi pi-th-large', route: '/admin/inicio', access: 'all', permission: 'home:read' },
    // ── Plataforma ──
    {
      id: 'negocios', label: 'Negocios y planes', icon: 'pi pi-building', access: 'all', scope: 'platform',
      children: [
        { label: 'Negocios', route: '/admin/businesses', icon: 'pi pi-building', access: 'all', permission: 'businesses:read' },
        { label: 'Planes', route: '/admin/plans', icon: 'pi pi-credit-card', access: 'all', permission: 'plans:read' },
        { label: 'Cobros y pagos', route: '/admin/subscriptions/payments', icon: 'pi pi-receipt', access: 'all', permission: 'subscriptions:read' },
        { label: 'Clientes (personas)', route: '/admin/persons', icon: 'pi pi-users', access: 'all', permission: 'persons:read' },
      ],
    },
    {
      id: 'acceso', label: 'Acceso', icon: 'pi pi-shield', access: 'all', scope: 'platform',
      children: [
        { label: 'Usuarios', route: '/admin/users', icon: 'pi pi-users', access: 'all', permission: 'users:read' },
        { label: 'Sistemas (Roles)', route: '/admin/systems', icon: 'pi pi-server', access: 'all', permission: 'systems:read' },
      ],
    },
    // ── Catálogos globales ──
    {
      id: 'impuestos', label: 'Impuestos y SRI', icon: 'pi pi-percentage', access: 'all', scope: 'catalog',
      children: [
        { label: 'Tarifas de IVA', route: '/admin/iva-rates', icon: 'pi pi-percentage', access: 'all', permission: 'iva-rates:read' },
        { label: 'Códigos de retención', route: '/admin/retention-codes', icon: 'pi pi-file-edit', access: 'all', permission: 'retention-codes:read' },
        { label: 'Parámetros normativos', route: '/admin/normative-parameters', icon: 'pi pi-book', access: 'all', permission: 'normative-parameters:read' },
      ],
    },
    {
      id: 'financial-institutions', label: 'Instituciones financieras', icon: 'pi pi-wallet', route: '/admin/financial-institutions',
      access: 'all', scope: 'catalog', permission: 'financial-institutions:read',
    },
    {
      id: 'master-accounting-plans', label: 'Planes de cuentas', icon: 'pi pi-sitemap', route: '/admin/master-accounting-plans',
      access: 'all', scope: 'catalog', permission: 'master-plans:read',
    },
  ];

  filteredMenuItems = computed(() => {
    const user = this.authService.currentUser();
    const isAdmin = !!user?.isAdmin;

    return this.menuItems
      .filter(item => {
        if (item.access === 'admin' && !isAdmin) return false;
        if (item.permission && !this.perms.can(item.permission)) return false;
        return true;
      })
      .map(item => {
        if (item.children) {
          const filteredChildren = item.children.filter(child => {
            if (child.access === 'admin' && !isAdmin) return false;
            if (child.permission && !this.perms.can(child.permission)) return false;
            return true;
          });
          return { ...item, children: filteredChildren };
        }
        return item;
      })
      .filter(item => {
        if (item.children && item.children.length === 0) return false;
        return true;
      })
      .map((item, i, all) => ({
        ...item,
        sectionStart: item.scope && all.findIndex((x) => x.scope === item.scope) === i ? (item.scope === 'platform' ? 'Plataforma' : 'Catálogos globales') : undefined,
      }));
  });

  get currentUser() {
    const user = this.authService.currentUser();
    if (!user) {
      return {
        name: 'Invitado',
        role: 'Invitado',
        initials: 'IN'
      };
    }
    const name = user.name || user.fullName || '';
    const initials = name
      .split(' ')
      .filter((n: string) => n.length > 0)
      .map((n: string) => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase() || 'US';
    return {
      name: name || user.email,
      role: user.isAdmin ? 'Administrador' : 'Cliente / Usuario',
      initials
    };
  }

  ngOnInit() {
    // Apagar el overlay de transición: ya estamos montados en el admin panel
    this.overlayService.hide();

    this.currentRoute = this.router.url;
    this.autoExpandActiveSubmenu(this.currentRoute);

    this.routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.currentRoute = event.urlAfterRedirects;
      this.autoExpandActiveSubmenu(this.currentRoute);
      this.showMobileMenu = false; // close mobile menu on navigation
    });
  }

  ngOnDestroy() {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
    if (this.overlayTimeout) {
      clearTimeout(this.overlayTimeout);
    }
  }

  toggleSidebar() {
    this.isCollapsed = !this.isCollapsed;
    // When collapsing, close expanded submenus
    if (this.isCollapsed) {
      Object.keys(this.expandedSubmenus).forEach(key => {
        this.expandedSubmenus[key] = false;
      });
      this.activeOverlayMenu = null;
    } else {
      this.autoExpandActiveSubmenu(this.currentRoute);
    }
  }

  toggleMobileMenu() {
    this.showMobileMenu = !this.showMobileMenu;
  }

  toggleProfileMenu() {
    this.showProfileMenu = !this.showProfileMenu;
  }



  toggleSubmenu(menuId: string, event: Event) {
    if (this.isCollapsed) {
      // In collapsed state, clicking does not expand inline.
      // Instead, we can show/toggle the floating overlay panel.
      const item = this.menuItems.find(m => m.id === menuId);
      if (item && item.children) {
        this.activeOverlayMenu = this.activeOverlayMenu?.id === menuId ? null : item;
      }
      event.stopPropagation();
      return;
    }

    event.preventDefault();
    this.expandedSubmenus[menuId] = !this.expandedSubmenus[menuId];
  }

  isSubmenuExpanded(menuId: string): boolean {
    return !!this.expandedSubmenus[menuId];
  }

  isMenuOrChildActive(item: MenuItem): boolean {
    if (item.route && this.currentRoute.startsWith(item.route)) {
      return true;
    }
    if (item.children) {
      return item.children.some(child => this.currentRoute.startsWith(child.route));
    }
    return false;
  }

  autoExpandActiveSubmenu(url: string) {
    this.menuItems.forEach(item => {
      if (item.children) {
        const hasActiveChild = item.children.some(child => url.startsWith(child.route));
        if (hasActiveChild && !this.isCollapsed) {
          this.expandedSubmenus[item.id] = true;
        }
      }
    });
  }

  // --- Hover event handlers for Collapsed sidebar overlay submenus ---
  onMouseEnterItem(item: MenuItem, event?: MouseEvent) {
    if (!this.isCollapsed) return;
    if (this.overlayTimeout) clearTimeout(this.overlayTimeout);

    if (item.children) {
      this.activeOverlayMenu = item;
      this.hoveredItemForTooltip = null;
      if (event) {
        const target = event.currentTarget as HTMLElement;
        if (target) this.overlayTop = target.getBoundingClientRect().top - 4; // la flechita apunta al centro del ícono
      }
    } else {
      this.activeOverlayMenu = null;
      this.hoveredItemForTooltip = item;
      if (event) {
        const target = event.currentTarget as HTMLElement;
        if (target) {
          const rect = target.getBoundingClientRect();
          this.tooltipTop = rect.top + (rect.height / 2) - 16;
        }
      }
    }
  }

  onMouseLeaveItem() {
    if (!this.isCollapsed) return;
    this.overlayTimeout = setTimeout(() => {
      this.activeOverlayMenu = null;
      this.hoveredItemForTooltip = null;
    }, 300);
  }

  onOverlayMouseEnter() {
    if (this.overlayTimeout) clearTimeout(this.overlayTimeout);
  }

  onOverlayMouseLeave() {
    this.onMouseLeaveItem();
  }

  get currentPageLabel(): string {
    const segment = this.currentRoute.split('/').pop() || '';
    return this.routeLabels[segment] || segment.replace(/-/g, ' ') || 'Inicio';
  }

  confirmLogout() {
    this.showProfileMenu = false;
    this.showLogoutConfirm = true;
  }

  logout() {
    this.showLogoutConfirm = false;
    this.authService.logout();
    this.overlayService.showAndNavigate(
      () => this.router.navigate(['/auth/login']),
      { message: 'Cerrando sesión...' }
    );
  }
}
